import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import FormHeader from 'components/FormHeader';
import FormTextInput from 'components/FormTextInput';
import FormFileUpload from 'components/FormFileUpload';
import { FormRow, UploadHint } from 'styles/CommonStyles';
import FormSelectInput from 'components/FormSelectInput';
import FormSubmit from 'components/FormSubmit';
import FormTracker from 'components/FormTracker';
import useSubmitActions from 'hooks/submit';
import useUploadActions from 'hooks/upload';
import { acceptedFiles, FormIdentifier } from 'lib/constants';
import { normalizePhoneNumber } from 'lib/utils';
import { patterns } from 'lib/validation';
import { showSubmitSuccessState } from 'state/atoms/submit';

const IdentityVerificationForm = () => {
  const { i18n, t } = useTranslation();
  const formMethods = useForm();
  const { submitForm } = useSubmitActions();
  const { uploadFiles } = useUploadActions();
  const setShowSuccess = useSetRecoilState(showSubmitSuccessState);

  const onSubmit = useCallback(
    (data: any) => {
      const { documents, phoneNumber, userType, ...otherData } = data;
      const formData = {
        formType: FormIdentifier.IDENTITY_VERIFICATION,
        phoneNumber: normalizePhoneNumber(phoneNumber),
        userType: userType.value,
        ...otherData,
      };
      const formSessionUuid = uuidv4();

      return uploadFiles(formSessionUuid, 'fraud', documents)
        .then((uploadedDocs) =>
          submitForm(
            formSessionUuid,
            { ...formData, documents: uploadedDocs },
            i18n.language
          )
        )
        .then(() => setShowSuccess(true));
    },
    [i18n.language, setShowSuccess, submitForm, uploadFiles]
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormHeader
          description={t('forms.identityVerification.description')}
          title={t('forms.identityVerification.name')}
        />
        <FormRow style={{ width: '50%' }}>
          <FormSelectInput
            name="userType"
            label={t<string>('labels.userType')}
            rules={{ required: t<string>('validation.required') }}
          >
            <option disabled value="">
              &nbsp;
            </option>
            <option value="customer">{t<string>('labels.customer')}</option>
            <option value="travel_agent">
              {t<string>('labels.travelAgent')}
            </option>
          </FormSelectInput>
        </FormRow>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.name')}
            name="name"
            rules={{
              pattern: {
                value: patterns.noSpecialCharacters,
                message: t<string>('validation.invalid'),
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormTextInput
            label={t<string>('labels.emailAddress')}
            name="email"
            rules={{
              pattern: {
                message: t<string>('validation.emailFormat'),
                value: patterns.emailAddress,
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.phoneNumber')}
            name="phoneNumber"
            rules={{
              pattern: {
                message: t<string>('validation.phoneNumberFormat'),
                value: patterns.phoneNumber,
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormTextInput
            label={t<string>('labels.loanIdOrAccountNumberOptional')}
            name="loanIdOrAccountId"
            rules={{
              pattern: {
                message: t<string>('validation.loanIdOrAccountNumberFormat'),
                value: patterns.loanIdOrAccountId,
              },
            }}
          />
        </FormRow>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.applicationNumber')}
            name="applicationId"
            rules={{ pattern: patterns.applicationId }}
            style={{ width: '50%' }}
          />
        </FormRow>
        <FormRow>
          <div>
            <FormFileUpload
              acceptTypes={acceptedFiles}
              ariaLabel={t('labels.uploadDocuments')}
              label={
                <>
                  {t('labels.uploadDocuments')}
                  <UploadHint>{t('labels.uploadIdCard')}</UploadHint>
                </>
              }
              maxFiles={2}
              name="documents"
              rules={{
                required: t<string>('validation.uploadDocumentsRequired'),
                validate: (docs: string[]) =>
                  docs?.length === 2 ||
                  t<string>('validation.uploadDocumentsRequired'),
              }}
            />
          </div>
        </FormRow>
        <FormSubmit formType={FormIdentifier.IDENTITY_VERIFICATION} />
        <FormTracker formType={FormIdentifier.IDENTITY_VERIFICATION} />
      </form>
    </FormProvider>
  );
};

export default IdentityVerificationForm;
